import { useEffect, useState, useRef } from "react";
import { api, FILE_URL } from "../../lib/api";
import { refreshInstallmentsSetting } from "../../lib/installments";
import { refreshPixDiscount } from "../../lib/pixDiscount";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export default function AdminSettings() {
  const [ann, setAnn] = useState({ text: "", active: true });
  const [hero, setHero] = useState({ supertitle: "", title: "", subtitle: "", cta_text: "", image_url: "" });
  const [slots, setSlots] = useState([]);
  const [imagesByKey, setImagesByKey] = useState({}); // key → storage path
  const [social, setSocial] = useState({ instagram_url: "", whatsapp_number: "" });
  const [about, setAbout] = useState(null);
  const [tryonLimit, setTryonLimit] = useState(30);
  // M244 — admin-controlled generation quality for the Try-On gpt-image-2
  // call. "high" is the safe default (~128s end-to-end, sharpest output);
  // "medium" is the speed-mode toggle (~60-80s estimated, less detail but
  // still production-acceptable). input_fidelity is pinned to "high" on the
  // backend regardless of this setting so jewelry positioning stays accurate.
  const [tryonQuality, setTryonQuality] = useState("high");
  const [consultorasWhatsapp, setConsultorasWhatsapp] = useState("");
  // M234 — up to 4 internal recipients for the new-sale alert. Blanks are
  // filtered out at save time; the server clamps to 4 max regardless.
  const [salesEmails, setSalesEmails] = useState(["", "", "", ""]);
  const [affinity, setAffinity] = useState(null);  // null until loaded
  // M248 — admin-uploaded favicon. `url` is the served URL (empty when no
  // override exists and the static /public/favicon.ico fallback is in use).
  const [faviconUrl, setFaviconUrl] = useState("");
  const faviconInputRef = useRef(null);
  // Max interest-free installments — single source of truth for cards, PDP
  // and Payment Brick. Editable here without any code change.
  const [maxInstallments, setMaxInstallments] = useState(6);
  // PIX à-vista discount percent (0-50). 0 disables the feature site-wide.
  const [pixDiscountPct, setPixDiscountPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    api.get("/settings/announcement").then((r) => setAnn(r.data));
    api.get("/settings/hero").then((r) => setHero(r.data));
    api.get("/settings/homepage-images").then((r) => {
      setSlots(r.data.slots || []);
      setImagesByKey(r.data.images || {});
    });
    api.get("/settings/social").then((r) => setSocial(r.data));
    api.get("/settings/about").then((r) => setAbout(r.data));
    api.get("/settings/tryon").then((r) => {
      setTryonLimit(r.data.monthly_limit ?? 30);
      setTryonQuality(r.data.quality === "medium" ? "medium" : "high");
    }).catch(() => {});
    api.get("/settings/consultoras-whatsapp").then((r) => setConsultorasWhatsapp(r.data.number || "")).catch(() => {});
    // Load sales notification emails (1–4 entries). Pad to 4 so the UI always
    // renders 4 input slots even when only 2 are configured.
    api.get("/settings/sales-notification-emails").then((r) => {
      const list = Array.isArray(r.data.emails) ? r.data.emails : [];
      const padded = [...list, "", "", "", ""].slice(0, 4);
      setSalesEmails(padded);
    }).catch(() => {});
    api.get("/admin/settings/affinity").then((r) => setAffinity(r.data)).catch(() => {});
    api.get("/settings/favicon").then((r) => setFaviconUrl(r.data.url || "")).catch(() => {});
    api.get("/settings/installments").then((r) => setMaxInstallments(Number(r.data.max) || 6)).catch(() => {});
    api.get("/settings/pix-discount").then((r) => setPixDiscountPct(Number(r.data.pct) || 0)).catch(() => {});
  }, []);

  const safeSave = async (fn, successMsg) => {
    setSaving(true);
    try {
      await fn();
      toast.success(successMsg);
    } catch (e) {
      const status = e?.response?.status;
      // 401 is handled globally by the api interceptor — don't double-toast.
      if (status !== 401) {
        const detail = e?.response?.data?.detail || e?.message || "tente novamente";
        toast.error(`Erro ao salvar: ${detail}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAnn = () => safeSave(() => api.put("/settings/announcement", ann), "Barra de anúncio atualizada");
  const saveHero = () => safeSave(() => api.put("/settings/hero", hero), "Hero atualizado");
  const saveSocial = () => safeSave(() => api.put("/settings/social", social), "Redes sociais atualizadas");
  const saveAbout = () => safeSave(() => api.put("/settings/about", about), "Página Sobre Nós atualizada");
  const saveTryonLimit = () =>
    safeSave(
      // PUT /settings/tryon takes BOTH monthly_limit and quality in a single
      // payload — keep them in sync so a partial update never wipes the other.
      () => api.put("/settings/tryon", {
        monthly_limit: Math.max(1, parseInt(tryonLimit, 10) || 1),
        quality: tryonQuality,
      }),
      "Configurações de Try-On atualizadas",
    );

  const saveTryonQuality = (next) => {
    setTryonQuality(next);
    safeSave(
      () => api.put("/settings/tryon", {
        monthly_limit: Math.max(1, parseInt(tryonLimit, 10) || 1),
        quality: next,
      }),
      `Qualidade alterada para ${next === "medium" ? "Rápida" : "Alta"}`,
    );
  };

  const saveConsultorasWhatsapp = () =>
    safeSave(
      () => api.put("/settings/consultoras-whatsapp", { number: consultorasWhatsapp }),
      "WhatsApp das consultoras atualizado",
    );

  const saveSalesEmails = () =>
    safeSave(
      // Server cleans blanks/duplicates/invalid entries — we send the full
      // 4-slot array as-is for clarity.
      () => api.put("/settings/sales-notification-emails", { emails: salesEmails }),
      "E-mails de notificação de venda atualizados",
    );

  const saveAffinity = (patch) =>
    safeSave(
      async () => {
        const r = await api.put("/admin/settings/affinity", patch);
        setAffinity(r.data);
      },
      "Mostruário Inteligente atualizado",
    );

  const uploadFor = async (slotKey, file) => {
    if (!file) return;
    setUploading(slotKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...imagesByKey, [slotKey]: r.data.path };
      setImagesByKey(next);
      // Auto-save: persist immediately so the user doesn't need a separate "Save" button.
      await api.put("/settings/homepage-images", { images: next });
      toast.success("Imagem atualizada ✓");
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 401) {
        toast.error("Erro ao enviar: " + (e?.response?.data?.detail || e?.message || ""));
      }
    } finally {
      setUploading(null);
    }
  };

  const removeImg = async (k) => {
    const next = { ...imagesByKey };
    delete next[k];
    setImagesByKey(next);
    try {
      await api.put("/settings/homepage-images", { images: next });
      toast.success("Imagem removida ✓");
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 401) {
        toast.error("Erro ao remover: " + (e?.response?.data?.detail || e?.message || ""));
      }
    }
  };

  // M248 — favicon upload. Uses a dedicated /settings/favicon endpoint that
  // accepts .ico/.png/.svg/.webp (the generic /uploads/image silently coerces
  // unknown extensions to .jpg which would break favicon caching). On
  // success we both update the preview AND patch the live <link rel="icon">
  // tags so the new icon takes effect immediately without a reload.
  const onFaviconChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("favicon");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/favicon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrl = r.data.url;
      setFaviconUrl(newUrl);
      // Live-swap the <link rel="icon"> tags so the browser picks up the new
      // favicon in this tab without a reload. The runtime hook in App.js
      // already handles new visitors; this is the immediate-feedback path
      // for the admin who just uploaded.
      try {
        const fullUrl = FILE_URL(newUrl.replace(/^\/api\/files\//, ""));
        document.querySelectorAll('link[rel*="icon"]').forEach((l) => {
          // append a cache-busting query so the browser actually refetches
          l.href = fullUrl + (fullUrl.includes("?") ? "&" : "?") + "v=" + Date.now();
        });
      } catch { /* non-fatal */ }
      toast.success("Favicon atualizado ✓");
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401) {
        toast.error("Erro ao enviar favicon: " + (err?.response?.data?.detail || err?.message || ""));
      }
    } finally {
      setUploading(null);
      if (faviconInputRef.current) faviconInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-settings">
      <p className="overline text-[#A8A8A8] mb-1">Conteúdo</p>
      <h1 className="font-display text-4xl mb-10">Configurações</h1>

      <div className="bg-white p-8 mb-6">
        <h3 className="font-display text-xl mb-5">Barra de anúncio</h3>
        <label className="overline text-[#A8A8A8] mb-2 block">Mensagem</label>
        <textarea rows={2} value={ann.text} onChange={(e) => setAnn({ ...ann, text: e.target.value })} className="w-full border border-[#E8E4DF] p-3 mb-4 focus:outline-none focus:border-[#B8A88A]" data-testid="ann-text"/>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={ann.active} onChange={(e) => setAnn({ ...ann, active: e.target.checked })} data-testid="ann-active" /> Ativa
        </label>
        <button onClick={saveAnn} disabled={saving} className="btn-primary" data-testid="save-ann">Salvar</button>
      </div>

      {/* M248 — Favicon. Preview do atual + botão de upload. Aceita
          .ico/.png/.svg/.webp/.jpg. O upload aplica imediatamente no site
          via runtime hook em App.js — sem precisar de redeploy. */}
      <div className="bg-white p-8 mb-6" data-testid="favicon-section">
        <h3 className="font-display text-xl mb-2">Favicon do site</h3>
        <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl leading-relaxed">
          Ícone exibido na aba do navegador e no atalho na tela inicial do celular.
          Recomendado: PNG quadrado de pelo menos 180×180 ou ICO multi-tamanho. Limite: 1MB.
        </p>
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 border border-[#E8E4DF] bg-[#FAF9F7] flex items-center justify-center p-2" data-testid="favicon-preview">
            <img
              src={faviconUrl ? FILE_URL(faviconUrl.replace(/^\/api\/files\//, "")) : "/favicon-32x32.png"}
              alt="Favicon atual"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", imageRendering: "auto" }}
            />
          </div>
          <div className="flex-1">
            <p className="text-xs text-[#A8A8A8] mb-3">
              {faviconUrl ? "Favicon personalizado ativo" : "Usando o favicon padrão da marca"}
            </p>
            <input
              ref={faviconInputRef}
              type="file"
              accept=".ico,.png,.svg,.webp,.jpg,.jpeg,image/x-icon,image/png,image/svg+xml,image/webp,image/jpeg"
              onChange={onFaviconChange}
              className="hidden"
              data-testid="favicon-file-input"
            />
            <button
              type="button"
              onClick={() => faviconInputRef.current?.click()}
              disabled={uploading === "favicon"}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="favicon-upload-btn"
            >
              <Upload size={14} />
              {uploading === "favicon" ? "Enviando..." : "Trocar favicon"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white p-8 mb-6" data-testid="installments-section">
        <h3 className="font-display text-xl mb-2">Pagamento — parcelamento sem juros</h3>
        <p className="text-sm text-[#6B6B6B] mb-5">
          Quantas vezes o cliente pode parcelar sem juros no cartão. Este valor
          aparece em <strong>todos os cards de produto</strong>, na página do
          produto e no checkout — é a única fonte de verdade.
          <br />
          <span className="text-xs text-[#A8A8A8]">
            Sua conta MP absorve os juros até este limite. Configure em conjunto
            com o painel do Mercado Pago (Parcelado vendedor).
          </span>
        </p>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-[200px]">
            <label className="overline text-[#A8A8A8] mb-2 block">
              Parcelas máximas sem juros
            </label>
            <input
              type="number"
              min={1}
              max={12}
              value={maxInstallments}
              onChange={(e) => setMaxInstallments(e.target.value)}
              className="input"
              data-testid="installments-max-input"
            />
          </div>
          <button
            onClick={() => {
              const n = Math.max(1, Math.min(12, parseInt(maxInstallments, 10) || 6));
              safeSave(
                () => api.put("/settings/installments", { max: n }),
                `Parcelamento atualizado para ${n}x sem juros`
              ).then(() => refreshInstallmentsSetting());
            }}
            disabled={saving}
            className="btn-primary"
            data-testid="save-installments"
          >
            Salvar
          </button>
        </div>
      </div>

      <div className="bg-white p-8 mb-6" data-testid="pix-discount-section">
        <h3 className="font-display text-xl mb-2">Pagamento — desconto PIX</h3>
        <p className="text-sm text-[#6B6B6B] mb-5">
          Percentual de desconto aplicado quando o cliente escolhe PIX.
          Aparece em cards, PDP, checkout e no valor cobrado no Mercado Pago.
          <br />
          <span className="text-xs text-[#A8A8A8]">
            Deixe <strong>0</strong> para desativar sem alterar o código.
          </span>
        </p>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-[200px]">
            <label className="overline text-[#A8A8A8] mb-2 block">
              Desconto PIX (%)
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={50}
                value={pixDiscountPct}
                onChange={(e) => setPixDiscountPct(e.target.value)}
                className="input pr-8"
                data-testid="pix-discount-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#A8A8A8] pointer-events-none">%</span>
            </div>
          </div>
          <button
            onClick={() => {
              const n = Math.max(0, Math.min(50, parseInt(pixDiscountPct, 10) || 0));
              safeSave(
                () => api.put("/settings/pix-discount", { pct: n }),
                n === 0
                  ? "Desconto PIX desativado"
                  : `Desconto PIX configurado em ${n}%`
              ).then(() => refreshPixDiscount());
            }}
            disabled={saving}
            className="btn-primary"
            data-testid="save-pix-discount"
          >
            Salvar
          </button>
        </div>
      </div>

      <div className="bg-white p-8 mb-6" data-testid="tryon-limit-section">
        <h3 className="font-display text-xl mb-2">Try-On — limite mensal</h3>
        <p className="text-sm text-[#6B6B6B] mb-5">
          Número máximo de simulações que cada cliente pode realizar por mês.
          Ao atingir este limite, o cliente verá uma tela informando que poderá voltar no próximo mês.
        </p>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-[200px]">
            <label className="overline text-[#A8A8A8] mb-2 block">Simulações por cliente / mês</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={tryonLimit}
              onChange={(e) => setTryonLimit(e.target.value)}
              className="input"
              data-testid="tryon-limit-input"
            />
          </div>
          <button onClick={saveTryonLimit} disabled={saving} className="btn-primary" data-testid="save-tryon-limit">
            Salvar
          </button>
        </div>
      </div>

      {/* M244 — Quality switch. Lets the operator A/B-test the gpt-image-2
          quality knob in production without a deploy. "high" is the
          fidelity-optimised baseline (~128s end-to-end), "medium" trades
          some detail for ~40-50% faster generations. input_fidelity stays
          pinned to high on the backend regardless so the jewelry
          positioning never degrades. */}
      <div className="bg-white p-8 mb-6" data-testid="tryon-quality-section">
        <h3 className="font-display text-xl mb-2">Try-On — qualidade da geração</h3>
        <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl leading-relaxed">
          Controla o parâmetro <code className="bg-[#FAF9F7] px-1.5">quality</code> enviado ao
          <code className="bg-[#FAF9F7] px-1.5">gpt-image-2</code> da OpenAI. A fidelidade às fotos de
          referência (cliente + joia) é sempre mantida em alta — só muda o nível de detalhe da imagem gerada.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
          <button
            type="button"
            onClick={() => saveTryonQuality("high")}
            className={`text-left p-5 border transition-colors ${tryonQuality === "high" ? "border-[#B8A88A] bg-[#FBF9F4]" : "border-[#E8E4DF] hover:border-[#B8A88A]"}`}
            data-testid="tryon-quality-high"
            disabled={saving}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-display text-lg">Alta</span>
              {tryonQuality === "high" && (
                <span className="text-[10px] uppercase tracking-wider text-[#B8A88A]">Ativa</span>
              )}
            </div>
            <p className="text-xs text-[#6B6B6B] leading-relaxed">
              Máxima riqueza de detalhe (brilho, textura, pedras). Tempo médio: <strong>~128s</strong> por simulação.
            </p>
          </button>
          <button
            type="button"
            onClick={() => saveTryonQuality("medium")}
            className={`text-left p-5 border transition-colors ${tryonQuality === "medium" ? "border-[#B8A88A] bg-[#FBF9F4]" : "border-[#E8E4DF] hover:border-[#B8A88A]"}`}
            data-testid="tryon-quality-medium"
            disabled={saving}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-display text-lg">Rápida</span>
              {tryonQuality === "medium" && (
                <span className="text-[10px] uppercase tracking-wider text-[#B8A88A]">Ativa</span>
              )}
            </div>
            <p className="text-xs text-[#6B6B6B] leading-relaxed">
              Geração otimizada para velocidade, com leve perda de detalhe. Estimativa: <strong>~60–80s</strong> por simulação.
            </p>
          </button>
        </div>
      </div>

      <div className="bg-white p-8 mb-6" data-testid="consultoras-whatsapp-section">
        <h3 className="font-display text-xl mb-2">Consultoras — WhatsApp</h3>
        <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl">
          Número usado pelo botão "Falar com uma consultora" na home e na página do Try-On.
          Formato sem espaços, com DDI + DDD + número (ex: <code className="bg-[#FAF9F7] px-1.5">5511987654321</code>).
        </p>
        <div className="flex items-end gap-4 max-w-md">
          <div className="flex-1">
            <Field label="Número do WhatsApp">
              <input
                value={consultorasWhatsapp}
                onChange={(e) => setConsultorasWhatsapp(e.target.value)}
                placeholder="5511987654321"
                className="input"
                data-testid="consultoras-whatsapp-input"
              />
            </Field>
          </div>
          <button onClick={saveConsultorasWhatsapp} disabled={saving} className="btn-primary" data-testid="save-consultoras-whatsapp">
            Salvar
          </button>
        </div>
      </div>

      {/* M234 — internal team alert recipients ----------------------------- */}
      <div className="bg-white p-8 mb-6" data-testid="sales-notification-emails-section">
        <h3 className="font-display text-xl mb-2">Notificação de venda — equipe</h3>
        <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl">
          E-mails que recebem o alerta automático quando uma compra é confirmada pelo Mercado Pago.
          Até <strong>4 endereços</strong>. Pode deixar em branco os que não usar.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          {salesEmails.map((value, idx) => (
            <Field key={idx} label={`E-mail ${idx + 1}`}>
              <input
                type="email"
                value={value}
                onChange={(e) => {
                  const next = [...salesEmails];
                  next[idx] = e.target.value;
                  setSalesEmails(next);
                }}
                placeholder="equipe@prizejoias.com"
                className="input"
                data-testid={`sales-email-${idx}`}
              />
            </Field>
          ))}
        </div>
        <div className="mt-5">
          <button onClick={saveSalesEmails} disabled={saving} className="btn-primary" data-testid="save-sales-emails">
            Salvar
          </button>
        </div>
      </div>

      <div className="bg-white p-8 mb-6">
        <h3 className="font-display text-xl mb-5">Hero — textos da homepage</h3>
        <div className="space-y-4">
          <Field label="Supertítulo"><input value={hero.supertitle} onChange={(e) => setHero({ ...hero, supertitle: e.target.value })} className="input" data-testid="hero-super"/></Field>
          <Field label="Título"><input value={hero.title} onChange={(e) => setHero({ ...hero, title: e.target.value })} className="input" data-testid="hero-title"/></Field>
          <Field label="Subtítulo"><input value={hero.subtitle} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} className="input" data-testid="hero-subtitle"/></Field>
          <Field label="Texto do botão"><input value={hero.cta_text} onChange={(e) => setHero({ ...hero, cta_text: e.target.value })} className="input" data-testid="hero-cta"/></Field>
        </div>
        <p className="text-xs text-[#A8A8A8] mt-3">A imagem do hero (Imagem 1) é configurada abaixo, na seção &ldquo;Imagens da homepage&rdquo;.</p>
        <button onClick={saveHero} disabled={saving} className="btn-primary mt-5" data-testid="save-hero">Salvar textos</button>
      </div>

      <div className="bg-white p-8 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-xl">Gerenciar Banners / Hero — Imagens da homepage</h3>
          <span className="overline text-[#B8A88A]">Auto-save ✓</span>
        </div>
        <p className="text-sm text-[#6B6B6B] mb-6">9 posições da homepage. Clique em <strong>Trocar imagem</strong> em cada slot — a alteração é salva e publicada automaticamente.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {slots.map((s) => {
            const path = imagesByKey[s.key];
            const url = path ? FILE_URL(path) : null;
            return (
              <ImageSlot
                key={s.key}
                slot={s}
                url={url}
                uploading={uploading === s.key}
                onUpload={(f) => uploadFor(s.key, f)}
                onRemove={() => removeImg(s.key)}
              />
            );
          })}
        </div>
      </div>

      <div className="bg-white p-8 mb-6" data-testid="social-section">
        <h3 className="font-display text-xl mb-5">Redes sociais (rodapé)</h3>
        <div className="space-y-4">
          <Field label="Link do Instagram (URL completa)">
            <input
              value={social.instagram_url}
              onChange={(e) => setSocial({ ...social, instagram_url: e.target.value })}
              className="input"
              placeholder="https://instagram.com/prizejoias"
              data-testid="social-instagram"
            />
          </Field>
          <Field label="WhatsApp (apenas o número com DDD, sem formatação)">
            <input
              value={social.whatsapp_number}
              onChange={(e) => setSocial({ ...social, whatsapp_number: e.target.value.replace(/\D/g, "") })}
              className="input"
              placeholder="11999990000"
              maxLength={11}
              data-testid="social-whatsapp"
            />
            <p className="text-xs text-[#A8A8A8] mt-1">O sistema monta automaticamente: <span className="font-mono">https://wa.me/55{social.whatsapp_number || "[número]"}</span></p>
          </Field>
        </div>
        <p className="text-xs text-[#A8A8A8] mt-3">Deixe em branco para esconder o link no rodapé.</p>
        <button onClick={saveSocial} disabled={saving} className="btn-primary mt-5" data-testid="save-social">Salvar</button>
      </div>

      {about && (
        <div className="bg-white p-8 mb-6" data-testid="about-section">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl">Página Sobre Nós</h3>
            <a href="/sobre" target="_blank" rel="noreferrer" className="overline text-[#B8A88A] hover:text-[#8C7B5E]" data-testid="about-preview-btn">
              Preview ↗
            </a>
          </div>
          <Field label="Foto institucional">
            {about.photo_path ? (
              <div className="flex items-center gap-4">
                <img src={FILE_URL(about.photo_path)} alt="" className="w-32 h-32 object-cover" />
                <div>
                  <p className="text-xs text-[#6B6B6B] mb-2">Foto enviada.</p>
                  <button
                    type="button"
                    onClick={() => setAbout({ ...about, photo_path: "" })}
                    className="text-xs text-red-600 hover:underline"
                    data-testid="about-remove-photo"
                  >
                    Remover foto
                  </button>
                </div>
              </div>
            ) : (
              <label className="btn-secondary cursor-pointer inline-flex" data-testid="about-upload-photo">
                <Upload size={14} className="mr-2"/> Enviar foto institucional
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const fd = new FormData();
                    fd.append("file", f);
                    const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setAbout({ ...about, photo_path: r.data.path });
                    toast.success("Foto enviada — salve para aplicar");
                  }}
                />
              </label>
            )}
          </Field>
          <div className="space-y-4 mt-6">
            <Field label="Título do hero">
              <input value={about.hero_title} onChange={(e) => setAbout({ ...about, hero_title: e.target.value })} className="input" data-testid="about-hero-title"/>
            </Field>
            <Field label="Introdução (parágrafo grande)">
              <textarea rows={3} value={about.intro} onChange={(e) => setAbout({ ...about, intro: e.target.value })} className="input" data-testid="about-intro"/>
            </Field>
            <Field label="Parágrafo 1 — história da família">
              <textarea rows={3} value={about.paragraph_1} onChange={(e) => setAbout({ ...about, paragraph_1: e.target.value })} className="input"/>
            </Field>
            <Field label="Parágrafo 2 — como selecionamos nossas peças">
              <textarea rows={3} value={about.paragraph_2} onChange={(e) => setAbout({ ...about, paragraph_2: e.target.value })} className="input"/>
            </Field>
            <Field label="Parágrafo 3 — nossos valores (em itálico, destaque)">
              <input value={about.paragraph_3} onChange={(e) => setAbout({ ...about, paragraph_3: e.target.value })} className="input"/>
            </Field>
            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-[#E8E4DF]">
              <div>
                <Field label="Valor 1 — título"><input value={about.value_1_title} onChange={(e) => setAbout({ ...about, value_1_title: e.target.value })} className="input"/></Field>
                <Field label="Valor 1 — descrição"><input value={about.value_1_desc} onChange={(e) => setAbout({ ...about, value_1_desc: e.target.value })} className="input"/></Field>
              </div>
              <div>
                <Field label="Valor 2 — título"><input value={about.value_2_title} onChange={(e) => setAbout({ ...about, value_2_title: e.target.value })} className="input"/></Field>
                <Field label="Valor 2 — descrição"><input value={about.value_2_desc} onChange={(e) => setAbout({ ...about, value_2_desc: e.target.value })} className="input"/></Field>
              </div>
              <div>
                <Field label="Valor 3 — título"><input value={about.value_3_title} onChange={(e) => setAbout({ ...about, value_3_title: e.target.value })} className="input"/></Field>
                <Field label="Valor 3 — descrição"><input value={about.value_3_desc} onChange={(e) => setAbout({ ...about, value_3_desc: e.target.value })} className="input"/></Field>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={saveAbout} disabled={saving} className="btn-primary" data-testid="save-about">Salvar Sobre Nós</button>
            <a href="/sobre" target="_blank" rel="noreferrer" className="btn-secondary">Ver página</a>
          </div>
        </div>
      )}

      <div className="bg-white p-6 md:p-8" data-testid="admin-affinity-section">
        <h2 className="font-display text-2xl mb-2">Mostruário Inteligente</h2>
        <p className="text-sm text-[#6B6B6B] mb-6 max-w-2xl">
          Sistema de recomendação por afinidade comportamental. Quando <strong>ativado</strong>,
          o catálogo, a home e o "Você também vai gostar" passam a mostrar peças
          ordenadas pelo perfil de cada cliente (clicks + lista de desejos), com uma
          dose de aleatoriedade que decai conforme ele interage mais na sessão.
          Quando <strong>desativado</strong>, o site exibe a seleção diversificada padrão (comportamento atual).
        </p>
        {affinity ? (
          <div className="space-y-5 max-w-2xl">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!affinity.enabled}
                onChange={(e) => saveAffinity({ enabled: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#B8A88A]"
                data-testid="affinity-enabled-toggle"
              />
              <span>
                <span className="font-medium">Ativar mostruário personalizado por afinidade</span>
                <span className="block text-xs text-[#6B6B6B] mt-1">
                  Recomendado ligar apenas após ~2 semanas de coleta de sinais
                  (Fase 1) com clientes reais navegando.
                </span>
              </span>
            </label>

            <details className="border border-[#E8E4DF] p-4">
              <summary className="cursor-pointer text-sm text-[#6B6B6B] hover:text-[#1A1A1A]">
                Parâmetros avançados (não mexa sem ler o playbook)
              </summary>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field label="ε inicial (0–1, default 0.85)">
                  <input
                    type="number" step="0.01" min="0" max="1"
                    defaultValue={affinity.epsilon_init}
                    onBlur={(e) => saveAffinity({ epsilon_init: parseFloat(e.target.value) })}
                    className="input"
                    data-testid="affinity-epsilon-init"
                  />
                </Field>
                <Field label="ε piso (0–1, default 0.25)">
                  <input
                    type="number" step="0.01" min="0" max="1"
                    defaultValue={affinity.epsilon_floor}
                    onBlur={(e) => saveAffinity({ epsilon_floor: parseFloat(e.target.value) })}
                    className="input"
                  />
                </Field>
                <Field label="k decay (default 0.18)">
                  <input
                    type="number" step="0.01" min="0" max="2"
                    defaultValue={affinity.k_decay}
                    onBlur={(e) => saveAffinity({ k_decay: parseFloat(e.target.value) })}
                    className="input"
                  />
                </Field>
                <Field label="Mín. sinais p/ aplicar (default 5)">
                  <input
                    type="number" step="1" min="1"
                    defaultValue={affinity.min_signals_for_affinity}
                    onBlur={(e) => saveAffinity({ min_signals_for_affinity: parseInt(e.target.value, 10) })}
                    className="input"
                  />
                </Field>
                <Field label="Teto por categoria (default 0.55)">
                  <input
                    type="number" step="0.01" min="0.2" max="1"
                    defaultValue={affinity.cap_per_category}
                    onBlur={(e) => saveAffinity({ cap_per_category: parseFloat(e.target.value) })}
                    className="input"
                  />
                </Field>
                <Field label="Dias de saciedade pós-compra (default 30)">
                  <input
                    type="number" step="1" min="0" max="365"
                    defaultValue={affinity.satiation_days}
                    onBlur={(e) => saveAffinity({ satiation_days: parseInt(e.target.value, 10) })}
                    className="input"
                  />
                </Field>
              </div>
              <p className="text-xs text-[#A8A8A8] mt-4">
                Dica: você também pode ligar via shell com{" "}
                <code className="bg-[#FAF9F7] px-2 py-0.5">db.settings.updateOne({"{key:'affinity'}"}, {"{$set:{enabled:true}}"}, {"{upsert:true}"})</code>
                {" "}ou pela variável de ambiente <code>AFFINITY_RECOMMENDATIONS_ENABLED=true</code>.
              </p>
            </details>
          </div>
        ) : (
          <p className="text-sm text-[#A8A8A8]">Carregando…</p>
        )}
      </div>

      <style>{`.input { width: 100%; border: 1px solid #E8E4DF; padding: 10px 14px; background: white; }
        .input:focus { outline: none; border-color: #B8A88A; }`}</style>
    </div>
  );
}

function ImageSlot({ slot, url, uploading, onUpload, onRemove }) {
  const inputRef = useRef(null);
  return (
    <div className="border border-[#E8E4DF] p-4" data-testid={`slot-${slot.key}`}>
      <p className="overline text-[#A8A8A8] mb-3">{slot.label}</p>
      <div className="aspect-[3/4] bg-[#FAF9F7] overflow-hidden relative mb-3">
        {url ? (
          <>
            <img src={url} alt={slot.label} className="w-full h-full object-cover" />
            <button
              onClick={onRemove}
              className="absolute top-2 right-2 bg-white p-1 hover:bg-red-50"
              aria-label="Remover"
              data-testid={`remove-${slot.key}`}
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#A8A8A8] text-xs">
            (usando imagem padrão)
          </div>
        )}
      </div>
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full btn-secondary text-xs py-2"
        data-testid={`upload-${slot.key}`}
      >
        <Upload size={12} className="mr-2"/> {uploading ? "Enviando..." : url ? "Trocar imagem" : "Enviar imagem"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline text-[#A8A8A8] mb-2 block">{label}</label>
      {children}
    </div>
  );
}
